// Small pieces both layouts of a document share: the status pill, the title,
// the muted meta line, the "Susiję (N)" toggle, the signers (chips on the
// desktop row, a list on the phone card), the action button styles and the
// signed-copy file picker. record-row.tsx (assets, leave requests) uses them too.
import { forwardRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { Check, Clock, Minus, X } from "lucide-react";
import { formatDateTime, formatDay, formatShortDay } from "./file-dates";
import type { FileItem } from "./file-item";
import type { RowSigner, SignerState, StatusPill } from "./file-row-model";

// Action buttons: an icon on the desktop row, a labelled footer button on the phone card.
export const ICON_BUTTON =
  "flex h-8 w-8 items-center justify-center rounded-md border border-zinc-200 bg-white text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800";
export const FOOTER_BUTTON =
  "flex min-h-11 flex-1 items-center justify-center rounded-md border border-zinc-200 bg-white px-2 py-2 text-center text-sm font-medium text-zinc-700";
// A link has no disabled state of its own.
export const DISABLED_LINK = "pointer-events-none opacity-40";

export type PillTone = "green" | "amber" | "red" | "solidRed" | "zinc";

const PILL_TONE_STYLE: Record<PillTone, string> = {
  green: "bg-green-50 text-green-800 ring-1 ring-inset ring-green-200",
  amber: "bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200",
  red: "bg-red-50 text-red-700 ring-1 ring-inset ring-red-200",
  solidRed: "bg-red-700 text-white",
  zinc: "bg-zinc-100 text-zinc-700 ring-1 ring-inset ring-zinc-200",
};

/** A short status word before a title. */
export function Pill({ tone, children }: { tone: PillTone; children: ReactNode }) {
  return (
    <span
      className={clsx(
        "inline-flex whitespace-nowrap rounded-md px-1.5 py-px align-[1px] text-xs font-semibold",
        PILL_TONE_STYLE[tone]
      )}
    >
      {children}
    </span>
  );
}

const STATUS_PILL_TONE: Record<StatusPill["kind"], PillTone> = {
  annulled: "solidRed",
  withdrawn: "amber",
  signed: "green",
  rejected: "red",
  approval_required: "amber",
  progress: "amber",
};

const PILL_LABEL_KEY: Record<Exclude<StatusPill["kind"], "progress">, string> = {
  annulled: "files_status_annulled",
  withdrawn: "files_status_withdrawn",
  signed: "files_status_signed",
  rejected: "files_status_rejected",
  approval_required: "files_status_approval_required",
};

export function StatusPillBadge({ status }: { status: StatusPill }) {
  const { t } = useTranslation();
  const label =
    status.kind === "progress"
      ? t("files_status_progress", { signed: status.signed, total: status.total })
      : t(PILL_LABEL_KEY[status.kind]);
  return <Pill tone={STATUS_PILL_TONE[status.kind]}>{label}</Pill>;
}

/** The document name; a link to its page when the viewer may open it. */
export function FileTitle({ title, url }: { title: string; url: string | null }) {
  if (!url) {
    return <span className="break-words font-semibold text-zinc-900">{title}</span>;
  }
  return (
    <Link
      to={url}
      className="break-words font-semibold text-zinc-900 hover:text-indigo-700 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600"
    >
      {title}
    </Link>
  );
}

export function RelatedToggle({
  count,
  expanded,
  onToggle,
  controls,
}: {
  count: number;
  expanded: boolean;
  onToggle: () => void;
  controls: string;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      className="text-indigo-600 hover:underline"
      aria-expanded={expanded}
      aria-controls={controls}
      onClick={onToggle}
    >
      {expanded ? "▾" : "▸"} {t("files_related", { count })}
    </button>
  );
}

/**
 * "2026-03-05 · Asta Jonaitienė · Nr. SF-12 · Category", items that are not
 * known are left out. `extra` (the related toggle on the desktop row) goes last.
 */
export function MetaLine({
  item,
  number,
  categoryTitle,
  extra,
}: {
  item: FileItem;
  number: string;
  categoryTitle: string;
  extra?: ReactNode;
}) {
  const { t } = useTranslation();
  const parts: { key: string; node: ReactNode }[] = [];
  const day = formatDay(item.createdAt);
  if (day) {
    parts.push({
      key: "date",
      node: <span title={formatDateTime(item.createdAt)}>{day}</span>,
    });
  }
  if (item.user?.name) parts.push({ key: "user", node: item.user.name });
  if (number) parts.push({ key: "number", node: t("files_number", { number }) });
  if (categoryTitle) parts.push({ key: "category", node: categoryTitle });
  if (extra) parts.push({ key: "extra", node: extra });

  return <MetaList parts={parts} />;
}

/** Muted facts in one wrapping line, separated by "·". */
export function MetaList({ parts }: { parts: { key: string; node: ReactNode }[] }) {
  return (
    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-zinc-500">
      {parts.map((part, index) => (
        <span key={part.key} className="inline-flex items-center gap-2">
          {index > 0 && (
            <span aria-hidden="true" className="text-zinc-300">
              ·
            </span>
          )}
          {part.node}
        </span>
      ))}
    </div>
  );
}

const SIGNER_ICON: Record<SignerState, typeof Check> = {
  signed: Check,
  pending: Clock,
  rejected: X,
  withdrawn: Minus,
};

const CHIP_STYLE: Record<SignerState, string> = {
  signed: "bg-green-50 text-green-800",
  pending: "bg-amber-50 text-amber-800",
  rejected: "bg-red-50 text-red-700",
  withdrawn: "bg-zinc-100 text-zinc-600",
};

const ICON_COLOR: Record<SignerState, string> = {
  signed: "text-green-700",
  pending: "text-amber-600",
  rejected: "text-red-600",
  withdrawn: "text-zinc-400",
};

const SIGNER_STATE_LABEL_KEY: Record<SignerState, string> = {
  signed: "files_signer_signed",
  pending: "files_signer_pending",
  rejected: "files_signer_rejected",
  withdrawn: "files_signer_withdrawn",
};

/** Exact time, and the reason for a decline, for the signer's tooltip. */
function signerTooltip(signer: RowSigner, t: (key: string, options?: object) => string) {
  const lines = [formatDateTime(signer.at)];
  if (signer.state === "rejected" && signer.reason) {
    lines.push(t("files_signer_decline_reason", { reason: signer.reason }));
  }
  return lines.filter(Boolean).join("\n");
}

type SignersProps = {
  signers: RowSigner[];
  // Only rows with a remindable signer (canRemind) need these.
  onRemind?: (signer: RowSigner) => void;
  remindingKey?: string | null;
};

/** Desktop: one chip per signer, "✓ name · 03-05", wrapping. */
export function SignerChips({ signers, onRemind, remindingKey }: SignersProps) {
  const { t } = useTranslation();
  return (
    <div className="mt-1.5 flex flex-wrap gap-1.5">
      {signers.map((signer) => {
        const Icon = SIGNER_ICON[signer.state];
        const day = formatShortDay(signer.at);
        return (
          <span
            key={signer.key}
            title={signerTooltip(signer, t)}
            className={clsx(
              "inline-flex items-center gap-1 whitespace-nowrap rounded-full py-0.5 pl-1.5 pr-2 text-xs",
              CHIP_STYLE[signer.state]
            )}
          >
            <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span className="sr-only">{t(SIGNER_STATE_LABEL_KEY[signer.state])}:</span>
            <span>{signer.name}</span>
            {day && (
              <span className="opacity-80">
                ·{" "}
                {signer.state === "pending" ? t("files_signer_since", { date: day }) : day}
              </span>
            )}
            {signer.canRemind && onRemind && (
              <button
                type="button"
                className="ml-1 border-l border-current pl-1.5 font-semibold underline underline-offset-2 disabled:opacity-50"
                disabled={remindingKey === signer.key}
                onClick={() => onRemind(signer)}
              >
                {t("files_remind")}
              </button>
            )}
          </span>
        );
      })}
    </div>
  );
}

/** Phone: one line per signer, "icon · name · state date", remind button below. */
export function SignerList({ signers, onRemind, remindingKey }: SignersProps) {
  const { t } = useTranslation();
  return (
    <ul className="flex flex-col gap-1.5 text-sm">
      {signers.map((signer) => {
        const Icon = SIGNER_ICON[signer.state];
        return (
          <li key={signer.key} title={signerTooltip(signer, t)}>
            <div className="flex items-center gap-2">
              <Icon
                className={clsx("h-4 w-4 shrink-0", ICON_COLOR[signer.state])}
                aria-hidden="true"
              />
              <span className="min-w-0 truncate">{signer.name}</span>
              <span className="ml-auto whitespace-nowrap text-xs text-zinc-500">
                {t(SIGNER_STATE_LABEL_KEY[signer.state])} {formatShortDay(signer.at)}
              </span>
            </div>
            {signer.canRemind && onRemind && (
              <button
                type="button"
                className="ml-6 mt-1 rounded-md border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 disabled:opacity-50"
                disabled={remindingKey === signer.key}
                onClick={() => onRemind(signer)}
              >
                {t("files_remind_by_email")}
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The hidden file picker behind "Įkelti pasirašytą dokumentą": the menu entry
 * clicks it through the ref, and the picked file goes to `onFile`.
 */
export const UploadSignedCopyInput = forwardRef<
  HTMLInputElement,
  { onFile: (file: File) => void }
>(function UploadSignedCopyInput({ onFile }, ref) {
  return (
    <input
      ref={ref}
      type="file"
      className="hidden"
      tabIndex={-1}
      aria-hidden="true"
      data-testid="upload-signed-copy"
      onChange={(event) => {
        const file = event.target.files?.[0];
        // Clear it, so picking the same file again still fires onChange.
        event.target.value = "";
        if (file) onFile(file);
      }}
    />
  );
});
